import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60' : 'bg-transparent'}`}
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="/"
            className="flex items-center gap-2 text-foreground-900 font-heading font-bold text-xl md:text-2xl whitespace-nowrap no-underline"
          >
            <span className="text-primary-500">YOYBUY</span>
            <span className="hidden sm:inline text-foreground-700 font-light">Spreadsheet</span>
          </a>

          <div className="hidden md:flex items-center gap-1">
            <a
              href="/"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors rounded-lg whitespace-nowrap no-underline"
            >
              Главная
            </a>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors rounded-lg whitespace-nowrap cursor-pointer bg-transparent border-0"
              >
                Категории
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 border border-background-200/70 rounded-xl overflow-hidden max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors no-underline whitespace-nowrap"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {cat.nameRu}
                      <span className="text-foreground-400 text-xs ml-2">({cat.name})</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="/article/yoybuy-spreadsheet-guide"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors rounded-lg whitespace-nowrap no-underline"
            >
              Руководство
            </a>

            <a
              href="/article/shopping-guide"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors rounded-lg whitespace-nowrap no-underline"
            >
              Гид по покупкам
            </a>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-semibold text-background-50 bg-secondary-500 hover:bg-secondary-600 rounded-full transition-colors whitespace-nowrap no-underline"
            >
              YOYBUY Sheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap no-underline"
            >
              Купить сейчас
              <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center"></i>
            </a>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center text-foreground-700 hover:text-primary-500 transition-colors bg-transparent border-0 cursor-pointer"
            >
              <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/60 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <a href="/" className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg no-underline">
              <i className="ri-home-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
              Главная
            </a>

            <a href="/article/shopping-guide" className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg no-underline">
              <i className="ri-shopping-bag-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
              Гид по покупкам
            </a>

            <a href="/article/yoybuy-spreadsheet-guide" className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg no-underline">
              <i className="ri-book-open-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
              Руководство
            </a>

            <div className="pt-2">
              <button
                onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
                className="flex items-center justify-between w-full px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 rounded-lg bg-transparent border-0 cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
                  Категории товаров
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isMobileCatOpen ? 'rotate-180' : ''} w-5 h-5 flex items-center justify-center`}></i>
              </button>
              {isMobileCatOpen && (
                <div className="grid grid-cols-2 gap-1 mt-1 px-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-2 py-2.5 text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors no-underline"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 shrink-0"></span>
                      {cat.nameRu}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-2 border-t border-background-200/60">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-secondary-600 hover:bg-secondary-50 rounded-lg no-underline"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-external-link-line text-secondary-500 w-5 h-5 flex items-center justify-center"></i>
                YOYBUY Sheet
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}